import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, BellRinging, X, CheckCircle, Clock, TrafficCone, XCircle, PaperPlaneTilt, WarningCircle, DeviceMobile, ChatCircleDots } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

const STUDENT_CATEGORIES = [
    {
        key: 'scores',
        title: 'Leskaart-updates',
        desc: 'Krijg een melding wanneer je instructeur scores of notities bijwerkt.',
        icon: CheckCircle,
        color: 'text-emerald-400 bg-emerald-500/10 border-emerald-400/30',
    },
    {
        key: 'scheduled',
        title: 'Nieuwe of gewijzigde les',
        desc: 'Direct een melding wanneer er een rijles voor je wordt ingepland of verplaatst.',
        icon: TrafficCone,
        color: 'text-[#D4A537] bg-[#D4A537]/10 border-[#D4A537]/40',
    },
    {
        key: 'reminder',
        title: '10-minuten lesreminder',
        desc: 'Een herinnering 10 minuten voor je rijles begint, zodat je nooit een afspraak vergeet.',
        icon: Clock,
        color: 'text-sky-400 bg-sky-500/10 border-sky-400/30',
    },
    {
        key: 'chat',
        title: 'Chatberichten',
        desc: 'Krijg een melding wanneer je instructeur je een bericht stuurt in de app.',
        icon: ChatCircleDots,
        color: 'text-violet-400 bg-violet-500/10 border-violet-400/30',
    },
];

const INSTRUCTOR_CATEGORIES = [
    {
        key: 'instructor_scheduled',
        title: 'Bevestiging nieuwe les',
        desc: 'Krijg op je telefoon een bevestiging zodra je een nieuwe rijles inplant.',
        icon: TrafficCone,
        color: 'text-[#D4A537] bg-[#D4A537]/10 border-[#D4A537]/40',
    },
    {
        key: 'cancellation',
        title: 'Annulering door leerling',
        desc: 'Direct een melding wanneer een leerling een rijles wil annuleren.',
        icon: XCircle,
        color: 'text-red-400 bg-red-500/10 border-red-400/30',
    },
    {
        key: 'chat',
        title: 'Chatberichten',
        desc: 'Krijg een melding wanneer een leerling je een bericht stuurt in de app.',
        icon: ChatCircleDots,
        color: 'text-violet-400 bg-violet-500/10 border-violet-400/30',
    },
];

function NotificationSettingsModal({ open, onClose, role }) {
    const [prefs, setPrefs] = useState({ scores: true, scheduled: true, reminder: true, instructor_scheduled: true, cancellation: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [subStatus, setSubStatus] = useState({ subscription_count: 0, vapid_configured: true });
    const [busyAction, setBusyAction] = useState(null); // 'enable' | 'test'
    const [feedback, setFeedback] = useState(null); // {type:'ok'|'error', text}
    const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
    const categories = role === 'instructor' ? INSTRUCTOR_CATEGORIES : STUDENT_CATEGORIES;

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [{ data: p }, { data: s }] = await Promise.all([
                    axios.get(`${API}/push/preferences`, { withCredentials: true }),
                    axios.get(`${API}/push/status`, { withCredentials: true }),
                ]);
                if (cancelled) return;
                setPrefs(p);
                setSubStatus(s);
            } catch (e) {
                if (!cancelled) setPrefs({ scores: true, scheduled: true, reminder: true });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const subscribeThisDevice = async () => {
        setBusyAction('enable');
        setFeedback(null);
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                throw new Error('Push wordt niet ondersteund door deze browser');
            }
            const reg = await navigator.serviceWorker.ready;
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== 'granted') {
                setFeedback({ type: 'error', text: 'Toestemming geweigerd. Schakel meldingen in via je browser-instellingen voor deze site.' });
                return;
            }
            const { data: { public_key } } = await axios.get(`${API}/push/vapid-public-key`, { withCredentials: true });
            // Force fresh subscription if older one exists (handles dead/stale subs)
            const existing = await reg.pushManager.getSubscription();
            if (existing) {
                try { await existing.unsubscribe(); } catch { /* ignore */ }
            }
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(public_key),
            });
            await axios.post(`${API}/push/subscribe`, sub.toJSON(), { withCredentials: true });
            const { data: s } = await axios.get(`${API}/push/status`, { withCredentials: true });
            setSubStatus(s);
            setFeedback({ type: 'ok', text: 'Dit toestel is gekoppeld voor meldingen.' });
        } catch (e) {
            setFeedback({ type: 'error', text: `Kon niet inschakelen: ${e?.message || 'onbekende fout'}` });
        } finally {
            setBusyAction(null);
        }
    };

    const sendTestPush = async () => {
        setBusyAction('test');
        setFeedback(null);
        try {
            const { data } = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
            setFeedback({
                type: 'ok',
                text: `Testmelding verstuurd naar ${data.sent} toestel${data.sent === 1 ? '' : 'len'}. Controleer je notificatiebalk binnen 10 seconden.`,
            });
            if (data.removed_dead) {
                const { data: s } = await axios.get(`${API}/push/status`, { withCredentials: true });
                setSubStatus(s);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || 'Onbekende fout';
            setFeedback({ type: 'error', text: detail });
        } finally {
            setBusyAction(null);
        }
    };

    const toggle = async (key, value) => {
        setSaving(key);
        setPrefs((prev) => ({ ...prev, [key]: value }));
        try {
            await axios.put(`${API}/push/preferences`, { [key]: value }, { withCredentials: true });
        } catch (e) {
            // Revert on failure
            setPrefs((prev) => ({ ...prev, [key]: !value }));
        } finally {
            setSaving(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-[#2D3748] border-[#4A5568] text-white max-w-md" data-testid="notification-settings-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <BellRinging size={22} weight="fill" className="text-[#D4A537]" />
                        Meldingen instellen
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Kies welke push-meldingen je wilt ontvangen. Je kunt dit altijd wijzigen.
                    </DialogDescription>
                </DialogHeader>

                {permission === 'denied' && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/40 p-3 text-xs text-red-200 flex items-start gap-2">
                        <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                        <span>Meldingen zijn <strong>geblokkeerd</strong> in je browser. Ga in Chrome naar Site-instellingen → Meldingen → leskaart-autopilot.nl → Toestaan.</span>
                    </div>
                )}

                {/* Device coupling status */}
                <div className={`rounded-lg p-3 border ${subStatus.subscription_count > 0
                    ? 'bg-emerald-500/10 border-emerald-400/40'
                    : 'bg-amber-500/10 border-amber-400/40'}`}
                    data-testid="device-status-card">
                    <div className="flex items-start gap-2.5">
                        <DeviceMobile size={18} weight="fill" className={subStatus.subscription_count > 0 ? 'text-emerald-400 mt-0.5' : 'text-amber-400 mt-0.5'} />
                        <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium ${subStatus.subscription_count > 0 ? 'text-emerald-200' : 'text-amber-200'}`}>
                                {subStatus.subscription_count > 0
                                    ? `${subStatus.subscription_count} toestel${subStatus.subscription_count === 1 ? '' : 'len'} gekoppeld`
                                    : 'Geen toestellen gekoppeld'}
                            </div>
                            <p className="text-[11px] text-gray-300 mt-0.5 leading-relaxed">
                                {subStatus.subscription_count > 0
                                    ? 'Je krijgt op de gekoppelde toestellen push-meldingen. Test hieronder of het echt werkt.'
                                    : 'Klik op "Inschakelen op dit toestel" om je telefoon/browser nu te koppelen. Zonder koppeling komen er geen pushes binnen.'}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2.5">
                                <Button
                                    size="sm"
                                    onClick={subscribeThisDevice}
                                    disabled={busyAction !== null || permission === 'denied'}
                                    className="bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] text-xs h-8"
                                    data-testid="notify-enable-device"
                                >
                                    {busyAction === 'enable' ? 'Bezig...' : (subStatus.subscription_count > 0 ? 'Opnieuw koppelen' : 'Inschakelen op dit toestel')}
                                </Button>
                                {subStatus.subscription_count > 0 && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={sendTestPush}
                                        disabled={busyAction !== null}
                                        className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10 text-xs h-8"
                                        data-testid="notify-send-test"
                                    >
                                        <PaperPlaneTilt size={12} weight="fill" className="mr-1.5" />
                                        {busyAction === 'test' ? 'Versturen...' : 'Stuur testmelding'}
                                    </Button>
                                )}
                            </div>
                            {feedback && (
                                <div
                                    className={`mt-2 text-[11px] rounded px-2 py-1.5 ${feedback.type === 'ok'
                                        ? 'bg-emerald-500/15 text-emerald-200'
                                        : 'bg-red-500/15 text-red-200'}`}
                                    data-testid="notify-feedback"
                                >
                                    {feedback.text}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-3 py-2">
                    {categories.map((cat) => {
                        const Icon = cat.icon;
                        const checked = !!prefs[cat.key];
                        return (
                            <div
                                key={cat.key}
                                className="flex items-start gap-3 p-3 rounded-lg bg-[#1A202C] border border-[#4A5568]"
                                data-testid={`notify-row-${cat.key}`}
                            >
                                <div className={`p-2 rounded-md border flex-shrink-0 ${cat.color}`}>
                                    <Icon size={18} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Label className="text-sm font-medium text-white cursor-pointer" htmlFor={`notify-switch-${cat.key}`}>
                                        {cat.title}
                                    </Label>
                                    <p className="text-xs text-gray-400 leading-relaxed mt-0.5">
                                        {cat.desc}
                                    </p>
                                </div>
                                <Switch
                                    id={`notify-switch-${cat.key}`}
                                    checked={checked}
                                    onCheckedChange={(v) => toggle(cat.key, v)}
                                    disabled={loading || saving === cat.key}
                                    className="mt-1 data-[state=checked]:bg-[#D4A537]"
                                    data-testid={`notify-toggle-${cat.key}`}
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="pt-2 text-[11px] text-gray-500 text-center">
                    Pushmeldingen werken alleen als de app is geïnstalleerd of geopend in je browser.
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Small bell button + modal, shown in the dashboard header for students.
 */
export default function NotificationSettingsButton({ role }) {
    const [open, setOpen] = useState(false);
    const supported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
    if (!supported) return null;
    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="text-[#D4A537] hover:text-[#E5B84A] transition-colors p-2 rounded-lg hover:bg-[#3D4A5C]"
                title="Meldingen instellen"
                data-testid="notification-settings-button"
                aria-label="Meldingen instellen"
            >
                <Bell size={20} weight="fill" />
            </button>
            <NotificationSettingsModal open={open} onClose={() => setOpen(false)} role={role} />
        </>
    );
}
